import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { DAY_VALUES, dayShort, muscleLabel, primaryMuscleOf } from 'components/play/constants';

// Constante de módulo: `items` es dependencia del useMemo que agrupa por día, y un `[]` en la
// firma se recrearía en cada render invalidando el agrupamiento.
const DEFAULT_ITEMS = [];

/**
 * OTRIX Play — la SEMANA de una rutina publicada en el foro (Lun..Dom).
 *
 * Se muestra siempre completa aunque haya días vacíos: quien la ve necesita saber qué días
 * entrena y con qué antes de adoptarla (un post sin la semana no dice qué se está adoptando).
 */
function SharedRoutineWeek({ items = DEFAULT_ITEMS }) {
  const { t } = useTranslation(['play']);

  const byDay = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      (map[it.day_of_week] = map[it.day_of_week] || []).push(it);
    });
    Object.keys(map).forEach((d) => map[d].sort((a, b) => (a.position || 0) - (b.position || 0)));
    return map;
  }, [items]);

  return (
    <Stack spacing={0.5} sx={{ mt: 1.5 }}>
      {DAY_VALUES.map((day) => {
        const dayItems = byDay[day] || [];
        const isRest = dayItems.length === 0;
        const muscle = primaryMuscleOf(dayItems);

        return (
          <Stack
            key={day}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: isRest ? 'surface.overlay' : 'surface.raised'
            }}>
            <Typography
              variant="caption"
              sx={{
                width: 34,
                flex: 'none',
                fontWeight: 800,
                textTransform: 'uppercase',
                color: isRest ? 'text.secondary' : 'gold.main'
              }}>
              {dayShort(t, day)}
            </Typography>

            {isRest ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('home.week.rest', { ns: 'play' })}
              </Typography>
            ) : (
              <>
                {muscle && (
                  <Chip
                    size="small"
                    label={muscleLabel(t, muscle)}
                    sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: 'gold.soft', color: 'gold.main', flex: 'none' }}
                  />
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 0 }} noWrap>
                  {dayItems.map((i) => i.exercise?.localized_name).filter(Boolean).join(' · ')}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                  {t('forum.exercises', { ns: 'play', count: dayItems.length })}
                </Typography>
              </>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

SharedRoutineWeek.propTypes = {
  /** SharedRoutineItem[] con day_of_week (el snapshot ya guarda la semana entera). */
  items: PropTypes.arrayOf(PropTypes.object)
};

export default SharedRoutineWeek;
